import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportCheckboxField,
  OtherReportFilterCard,
  OtherReportFilterSection,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const NonFuelSalesReport = (props) => {
  const [meta, setMeta] = useState({ vendors: [], products: [] });
  const [inputValues, setInputValues] = useState({ vendor: [], product: [], summary: false });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/product_sales_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Non-Fuel Sales', error.message, 'error'));
  }, []);

  const toggleSelection = (field, id) => {
    const selected = new Set((inputValues[field] || []).map(String));
    const key = String(id);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    setInputValues({ ...inputValues, [field]: Array.from(selected) });
  };

  const setSelection = (field, ids) => {
    setInputValues({ ...inputValues, [field]: (ids || []).map(String) });
  };

  const buildPayload = () => {
    const payload = {
      from_date: inputValues.start_date,
      to_date: inputValues.end_date,
      vendor: inputValues.vendor,
      product: inputValues.product,
    };
    if (inputValues.summary) {
      payload.summary = 1;
    }
    return payload;
  };

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    if (!inputValues.vendor?.length && !inputValues.product?.length) {
      swal('Please select at least one vendor or product', '', 'warning');
      return false;
    }
    return true;
  };

  const loadReport = async () => {
    if (!validateFilters()) {
      return;
    }

    setLoading(true);
    setReport(null);

    try {
      setReport(await fetchReportJson(`${API_URL}/product_sales_statement`, buildPayload()));
    } catch (error) {
      swal('Non-Fuel Sales', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/product_sales_statement/pdf`,
        buildPayload(),
        'product_sales_statement.pdf',
      );
    } catch (error) {
      swal('Non-Fuel Sales', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const rows = useMemo(() => {
    return (report?.rows || []).filter(
      (row) => Number(row.opening_stock ?? 0) >= 0 || report?.is_summary
    );
  }, [report]);

  const totalsRow = useMemo(() => {
    if (report?.totals_row) {
      return report.totals_row;
    }

    return {
      label: 'Total',
      purchased_quantity: rows.reduce(
        (sum, row) => sum + Number(row.purchased_quantity || 0),
        0
      ),
      purchase_return_quantity: rows.reduce(
        (sum, row) => sum + Number(row.purchase_return_quantity || 0),
        0
      ),
      sales_during_the_period: rows.reduce(
        (sum, row) => sum + Number(row.sales_during_the_period || 0),
        0
      ),
      sales_value: rows.reduce((sum, row) => sum + Number(row.sales_value || 0), 0),
    };
  }, [report, rows]);

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Non-Fuel Sales Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                    <OtherReportCheckboxField
                      id="nfs-summary-toggle"
                      label="Summary mode"
                      checked={inputValues.summary}
                      onChange={(checked) => setInputValues({ ...inputValues, summary: checked })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
              <OtherReportFilterSection title="Filters" description="Select at least one vendor or product.">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="other-report-filter-section-title d-block mb-2">Vendors</label>
                    <OtherReportSearchableCheckList
                      items={meta.vendors || []}
                      selectedIds={inputValues.vendor || []}
                      onToggle={(id) => toggleSelection('vendor', id)}
                      onChange={(ids) => setSelection('vendor', ids)}
                      searchPlaceholder="Search vendors…"
                      getId={(item) => String(item.id)}
                      getLabel={(item) => item.name}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="other-report-filter-section-title d-block mb-2">Products</label>
                    <OtherReportSearchableCheckList
                      items={meta.products || []}
                      selectedIds={inputValues.product || []}
                      onToggle={(id) => toggleSelection('product', id)}
                      onChange={(ids) => setSelection('product', ids)}
                      searchPlaceholder="Search products…"
                      getId={(item) => String(item.id)}
                      getLabel={(item) => item.product_name}
                    />
                  </div>
                </div>
              </OtherReportFilterSection>
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating sales statement" />}
              {report && !loading && (
              <div className="form-wrap balace-sheetwrap">
                <div className="row mb-3">
                  <div className="col-md">
                    <h2 className="table-title mb-1">{report.pump_name}</h2>
                    <h4 className="table-title mb-1">{report.type}</h4>
                    <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                    <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                  </div>
                </div>
                <div className="other-report-table-wrap">
                <table className="other-report-table">
                  <thead>
                    {report.is_summary ? (
                      <tr>
                        <th>Vendor</th>
                        <th>Product</th>
                        <th>Quantity sold (Qty)</th>
                        <th>Selling price/unit</th>
                        <th>Sales value</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Vendor</th>
                        <th>Product</th>
                        <th>HSN Code</th>
                        <th>Date</th>
                        <th>Opening stock</th>
                        <th>Stock addition</th>
                        <th>Purchase return</th>
                        <th>Quantity sold (Qty)</th>
                        <th>Closing stock</th>
                        <th>Selling price/unit</th>
                        <th>Sales value</th>
                        <th>Average monthly sale</th>
                        <th>Number of days stock in hand</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={`row-${i}`}>
                        {report.is_summary ? (
                          <>
                            <td>{row.all_ven_name || ''}</td>
                            <td>{row.product_name}</td>
                            <td className="report-amount">{row.sales_during_the_period}</td>
                            <td className="report-amount">{reportNumberFormat(row.selling_price)}</td>
                            <td className="report-amount">{reportNumberFormat(row.sales_value)}</td>
                          </>
                        ) : (
                          <>
                            <td>{row.all_ven_name || ''}</td>
                            <td>{row.product_name}</td>
                            <td>{row.hsn_code || ''}</td>
                            <td>{formatReportDate(row.ac_date)}</td>
                            <td className="report-amount">{row.opening_stock}</td>
                            <td className="report-amount">{row.purchased_quantity}</td>
                            <td className="report-amount">{row.purchase_return_quantity ?? 0}</td>
                            <td className="report-amount">{row.sales_during_the_period}</td>
                            <td className="report-amount">{row.closing_stock}</td>
                            <td className="report-amount">{reportNumberFormat(row.selling_price)}</td>
                            <td className="report-amount">{reportNumberFormat(row.sales_value)}</td>
                            <td className="report-amount">{row.avg_month_sales ?? 0}</td>
                            <td className="report-amount">{row.no_of_days_stock_in_hand ?? 0}</td>
                          </>
                        )}
                      </tr>
                    ))}
                    {rows.length > 0 && (
                      <tr className="other-report-totals-row">
                        {report.is_summary ? (
                          <>
                            <td colSpan={2}>{totalsRow.label || 'Total'}</td>
                            <td className="report-amount">{totalsRow.sales_during_the_period}</td>
                            <td />
                            <td className="report-amount">{reportNumberFormat(totalsRow.sales_value)}</td>
                          </>
                        ) : (
                          <>
                            <td colSpan={5}>{totalsRow.label || 'Total'}</td>
                            <td className="report-amount">{totalsRow.purchased_quantity}</td>
                            <td className="report-amount">{totalsRow.purchase_return_quantity ?? 0}</td>
                            <td className="report-amount">{totalsRow.sales_during_the_period}</td>
                            <td />
                            <td />
                            <td className="report-amount">{reportNumberFormat(totalsRow.sales_value)}</td>
                            <td colSpan={2} />
                          </>
                        )}
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default NonFuelSalesReport;
